import React from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import { MAX_CORE_SCREEN_WIDTH, MAX_SMALL_SCREEN_WIDTH } from '../../constants';
import './styles.css';

const MIN_BAR_WIDTH = 60;
const MAX_BAR_WIDTH = 200;
const MAX_BAR_HEIGHT = 250;

class MultipleBarChart extends React.PureComponent {
  static propTypes = {
    chartData: PropTypes.arrayOf(
      PropTypes.shape({
        bars: PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.number.isRequired,
            label: PropTypes.string,
            color: PropTypes.string,
            width: PropTypes.number,
            dashedPattern: PropTypes.boolean,
          }),
        ),
        label: PropTypes.string,
      }),
    ),
    maxBarWidth: PropTypes.number,
    maxBarHeight: PropTypes.number,
  };

  static defaultProps = {
    maxBarHeight: MAX_BAR_HEIGHT,
  };

  constructor(props) {
    super(props);
    this.state = {
      windowWidth: window.innerWidth,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = debounce(() => {
    this.setState({ windowWidth: window.innerWidth });
  }, 500);

  getMaxBarWidth = () => {
    const { chartData, maxBarWidth } = this.props;
    if (maxBarWidth) {
      return maxBarWidth;
    }

    const numberOfMultiBars = chartData.length;
    let windowWidth =
      this.state.windowWidth < MAX_CORE_SCREEN_WIDTH
        ? this.state.windowWidth
        : MAX_CORE_SCREEN_WIDTH;
    windowWidth = windowWidth - 70; // delta for screen top level padding and margin;

    let calculatedWidth = Math.floor(windowWidth / numberOfMultiBars) - 100; // keeping space for labels
    if (calculatedWidth > MAX_BAR_WIDTH) {
      calculatedWidth = MAX_BAR_WIDTH;
    }
    if (calculatedWidth < MIN_BAR_WIDTH) {
      calculatedWidth = MIN_BAR_WIDTH;
    }

    return calculatedWidth;
  };

  getMaxBarValue = () => {
    const { chartData } = this.props;
    let maxBarValue = 0;
    chartData.forEach((data) => {
      if (data.bars && data.bars.length > 0) {
        data.bars.forEach((bar) => {
          if (bar && bar.value > maxBarValue) {
            maxBarValue = bar.value;
          }
        });
      }
    });
    return maxBarValue;
  };

  getBarLabelStyles = (barData, index, barHeightsData, maxBarWidth) => {
    const styles = {};
    if (barData && barHeightsData[index]) {
      const barWidth = barData.width || maxBarWidth;
      const deltaWidth = index * Math.floor(barWidth / 10);
      styles.top = `${
        barHeightsData[index].height - barHeightsData[index].labelHeight
      }px`;
      if (window.innerWidth < MAX_SMALL_SCREEN_WIDTH) {
        styles.width = '70px';
        styles.left = `-${75 + deltaWidth / 2}px`;
      } else {
        styles.left = `-${85 + deltaWidth / 2}px`;
      }
    }
    return styles;
  };

  getBarStyles = (barData, index, barHeightsData, maxBarWidth) => {
    const styles = {};
    if (barData && barHeightsData[index]) {
      const barWidth = barData.width || maxBarWidth;
      const deltaWidth = index * Math.floor(barWidth / 10);

      styles.width = `${barWidth - deltaWidth}px`;
      styles.height = `${barHeightsData[index].height}px`;

      styles.backgroundColor = barData.color;
      styles.position = 'absolute';
      styles.bottom = '0';
      styles.right = `${deltaWidth / 2}px`;
    }
    return styles;
  };

  getBarAndLabelHeights = (maxBarValue) => {
    const { maxBarHeight, chartData } = this.props;
    let labelLineHeight = window.innerWidth < MAX_SMALL_SCREEN_WIDTH ? 14 : 16;

    return chartData.map((data) => {
      const heights = [];
      if (data.bars && data.bars.length > 0) {
        let prevLabelHeight = 0;
        for (let i = data.bars.length - 1; i >= 0; i--) {
          let labelHeight = 0;
          let height = 0;
          if (!!data.bars[i].value && data.bars[i].value > 0) {
            height = Math.floor((maxBarHeight * data.bars[i].value) / maxBarValue);
            labelHeight = height;
            if (labelHeight >= 0 && labelHeight < prevLabelHeight + labelLineHeight) {
              labelHeight = prevLabelHeight + labelLineHeight;
            }
            prevLabelHeight = labelHeight;
          }
          heights.push({ height, labelHeight });
        }
        heights.reverse();
      }
      return heights;
    });
  };

  getBarSvgPattern = (barData, maxBarWidth, maxBarValue) => {
    const { maxBarHeight } = this.props;
    const barWidth = barData.width || maxBarWidth;
    const barHeight = Math.floor((maxBarHeight * barData.value) / maxBarValue);

    const max = Math.max(barHeight, barWidth);
    const min = 0;
    const delta = 10;
    const lines = [];
    let x2 = max - delta;
    let y1 = min + delta;
    let y2 = max;

    for (let x1 = min; x1 <= max; x1 = x1 + delta) {
      lines.push(
        <line
          key={`left-${y2}`}
          x1={x1}
          y1={min}
          x2={max}
          y2={y2}
          className="multibar-bar-svg-line"
        />,
      );
      y2 = y2 - delta;
      if (x1 > min) {
        lines.push(
          <line
            key={`right-${y1}`}
            x1={min}
            y1={y1}
            x2={x2}
            y2={max}
            className="multibar-bar-svg-line"
          />,
        );
        y1 = y1 + delta;
        x2 = x2 - delta;
      }
    }

    const svgStyle = {
      top: barHeight < 19 ? `${barHeight - 19}px` : '0',
    };

    return (
      <svg
        height={barHeight}
        width={barWidth}
        style={svgStyle}
        className="multibar-bar-svg-pattern"
      >
        {lines}
      </svg>
    );
  };

  render() {
    const { chartData } = this.props;
    if (!chartData || chartData.length <= 0) {
      return null;
    }

    const maxBarWidth = this.getMaxBarWidth();
    const maxBarValue = this.getMaxBarValue();
    const barHeightsData = this.getBarAndLabelHeights(maxBarValue);
    const minBlockWidth = maxBarWidth + 100;

    return (
      <div data-cy="multibar-chart-container" className="multibar-chart-container">
        <div
          className="plot-area"
          style={{ minWidth: `${minBlockWidth * chartData.length}px` }}
        >
          <hr className="plot-x-axis-line" />
          {chartData.map((data, index) => (
            <div
              key={`${data.label}-${index}`}
              className="multibar-block-container"
              data-cy="multibar-block-container"
              style={{ minWidth: minBlockWidth }}
            >
              {data.bars.map((bar, barIndex) =>
                bar.value ? (
                  <div
                    key={`${bar.label}-${index}`}
                    className="multibar-bar-block-container"
                  >
                    <div
                      className="multibar-bar-block"
                      data-cy="multibar-bar-block"
                      style={this.getBarStyles(
                        bar,
                        barIndex,
                        barHeightsData[index],
                        maxBarWidth,
                      )}
                    >
                      <div
                        className="multibar-bar-block-label"
                        data-cy="multibar-bar-block-label"
                        style={this.getBarLabelStyles(
                          bar,
                          barIndex,
                          barHeightsData[index],
                          maxBarWidth,
                        )}
                      >
                        {bar.label}
                      </div>
                      {bar.dashedPattern &&
                        this.getBarSvgPattern(bar, maxBarWidth, maxBarValue)}
                    </div>
                  </div>
                ) : null,
              )}
              <div className="multibar-block-label" style={{ width: maxBarWidth }}>
                {data.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
}

export default MultipleBarChart;
